import { AlertCircle, MoreVerticalIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useRemoveEventCategory } from "@/features/event-categories/api/remove-event-category";
import { PublicEventCategory } from "@classview/shared-types";
import { useQueryClient } from "@tanstack/react-query";

import { fetchEventCategoriesKey } from "../api/fetch-event-categories";

type EventCategoriesRowActionsProps = {
  eventCategoryId: string;
  organisationId: string;
};

export function EventCategoriesRowActions(
  props: EventCategoriesRowActionsProps,
) {
  const { organisationId, eventCategoryId } = props;
  const queryClient = useQueryClient();

  const {
    mutateAsync: deleteEventCategory,
    isPending,
    isSuccess,
    isError,
    error,
    reset,
  } = useRemoveEventCategory({
    onSuccess: () => {
      queryClient.setQueryData(
        fetchEventCategoriesKey({ organisationId: organisationId }),
        (oldData: PublicEventCategory[]) =>
          oldData.filter(
            (eventCategory) => eventCategory.id !== eventCategoryId,
          ),
      );
    },
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!isDeleting) {
      reset();
    }
  }, [isDeleting, reset]);

  const handleDelete = async () => {
    try {
      await deleteEventCategory({
        orgId: organisationId,
        eventCategoryId: eventCategoryId,
      });
      setIsDeleting(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dialog onOpenChange={setIsDeleting} open={isDeleting}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DialogTrigger asChild>
            <DropdownMenuItem className="text-destructive">
              Delete
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
          <DialogDescription>
            This action cannot be undone. Are you sure you want to permanently
            delete this event category?
          </DialogDescription>
        </DialogHeader>
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending || isSuccess}
            isLoading={isPending || isSuccess}
            isAnimated
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
